import { Button, Input, Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import { CloudUpload, Upload } from 'lucide-react';
import { TAXONOMY_MARKET_OPTIONS, TAXONOMY_PLATFORM_OPTIONS } from '@/entities/taxonomy';
import { MultiSelectDropdown } from '../../../TaxonomyList/components/CreateTaxonomyModal/MultiSelectDropdown';

interface SchemaPropertiesProps {
  name: string;
  description: string;
  scope: string;
  markets: string[];
  platforms: string[];
  mediaFieldCount: number;
  contentFieldCount: number;
  lastModifiedBy: string | null;
  lastModifiedAt: string;
  readOnly: boolean;
  /**
   * Distinct from `readOnly`, which is also true for someone who CAN manage but
   * is browsing a published version — they get the disabled control, because
   * unpublishing makes it live again. Someone without the permission never gets
   * it back, so a control they can only look at is noise.
   */
  canManage: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onMarketsChange: (value: string[]) => void;
  onPlatformsChange: (value: string[]) => void;
}

/** What a picker's selection reads as to someone who cannot open the picker. */
function SelectedValues({ values }: { values: string[] }) {
  return (
    <span className="text-sm text-foreground">{values.length > 0 ? values.join(', ') : '—'}</span>
  );
}

// Getters, not values: a resolved label would freeze at import.
const SCOPE_LABEL: Record<string, string> = {
  get workspace() {
    return i18n.t('knowledge:taxonomy.schemaProperties.workspace');
  },
  get organization() {
    return i18n.t('knowledge:taxonomy.schemaProperties.org');
  },
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * Schema Properties card for the builder: editable name/description, markets &
 * platforms (dropdown multi-selects), plus read-only scope, field counts and
 * last-modified. Source actions (Upload / Import from Claravine) are rendered as
 * disabled placeholders — the ingestion flows aren't built yet.
 */
export function SchemaProperties(props: SchemaPropertiesProps) {
  const { t } = useTranslation('knowledge');
  return (
    <div className="flex flex-col gap-5 rounded-xl border border-border-light bg-card p-5">
      <h2 className="text-sm font-semibold text-foreground">
        {t('taxonomy.schemaProperties.schemaProperties')}
      </h2>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label={t('taxonomy.schemaProperties.schemaName')}>
          <Input
            value={props.name}
            disabled={props.readOnly}
            maxLength={255}
            onChange={(e) => props.onNameChange(e.target.value)}
            className="bg-input"
          />
        </Field>
        <Field label={t('taxonomy.schemaProperties.description')}>
          {/*
            Both bounds mirror the draft-save DTO. Overshooting either here is
            worse than a rejected form: autosave stores the value, then every
            later save of the same draft fails validation on a field the editor
            is no longer touching.
          */}
          <Input
            value={props.description}
            disabled={props.readOnly}
            placeholder={t('taxonomy.schemaProperties.addDescription')}
            maxLength={2000}
            onChange={(e) => props.onDescriptionChange(e.target.value)}
            className="bg-input"
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-4 border-t border-border-light pt-4 md:grid-cols-3 lg:grid-cols-6">
        <Field label={t('taxonomy.schemaProperties.scope')}>
          <span className="text-sm text-foreground">{SCOPE_LABEL[props.scope] ?? props.scope}</span>
        </Field>
        <Field label={t('taxonomy.schemaProperties.markets')}>
          {props.canManage ? (
            <MultiSelectDropdown
              compact
              value={props.markets}
              onChange={props.onMarketsChange}
              options={TAXONOMY_MARKET_OPTIONS}
              placeholder={t('taxonomy.schemaProperties.select')}
              disabled={props.readOnly}
            />
          ) : (
            <SelectedValues values={props.markets} />
          )}
        </Field>
        <Field label={t('taxonomy.schemaProperties.platforms')}>
          {props.canManage ? (
            <MultiSelectDropdown
              compact
              value={props.platforms}
              onChange={props.onPlatformsChange}
              options={TAXONOMY_PLATFORM_OPTIONS}
              placeholder={t('taxonomy.schemaProperties.select')}
              disabled={props.readOnly}
            />
          ) : (
            <SelectedValues values={props.platforms} />
          )}
        </Field>
        <Field label={t('taxonomy.schemaProperties.fields')}>
          <span className="text-sm text-foreground">
            {props.mediaFieldCount} {t('taxonomy.schemaProperties.media')} {props.contentFieldCount}{' '}
            {t('taxonomy.schemaProperties.content')}
          </span>
        </Field>
        <Field label={t('taxonomy.schemaProperties.lastModified')}>
          <div className="flex flex-col">
            {props.lastModifiedBy && (
              <span className="text-sm text-foreground">{props.lastModifiedBy}</span>
            )}
            <span
              className={
                props.lastModifiedBy ? 'text-xs text-muted-foreground' : 'text-sm text-foreground'
              }
            >
              {new Date(props.lastModifiedAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
        </Field>
        <Field label={t('taxonomy.schemaProperties.source')}>
          <div className="flex flex-wrap gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focus host for the tooltip, since the button it wraps is disabled */}
                <span tabIndex={0}>
                  <Button variant="outline" size="mini" disabled>
                    <Upload className="size-3.5" />
                    {t('taxonomy.schemaProperties.upload')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{t('taxonomy.schemaProperties.comingSoon')}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focus host for the tooltip, since the button it wraps is disabled */}
                <span tabIndex={0}>
                  <Button variant="outline" size="mini" disabled>
                    <CloudUpload className="size-3.5" />
                    {t('taxonomy.schemaProperties.import')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{t('taxonomy.schemaProperties.comingSoon')}</TooltipContent>
            </Tooltip>
          </div>
        </Field>
      </div>
    </div>
  );
}

SchemaProperties.displayName = 'SchemaProperties';
